/**
 * 로그인 후 진입 경로 결정 헬퍼
 * @MX:ANCHOR: [AUTO] 로그인/라우터 가드 공통 진입경로 결정 — signInMixin, ForcePasswordChange, router guard 3곳에서 호출
 * @MX:REASON: fan_in >= 3, 변경 시 로그인·강제변경·URL직접진입 세 흐름 동시 영향
 */

export const NO_MENU_PATH = '/no-menu';

/**
 * privileges 문서에서 앱 진입 경로를 결정한다.
 * @param {Object|null} priv - getPrivileges 반환값 (category 배열 포함)
 * @returns {string} 첫 메뉴 링크('/'로 시작) 또는 NO_MENU_PATH
 */
export function resolveLandingPath(priv) {
  const link = priv?.category?.[0]?.menu?.[0]?.link;
  if (link && link.startsWith('/')) {
    return link;
  }
  return NO_MENU_PATH;
}

/**
 * store에서 회사정보·권한을 fetch한 뒤 진입 경로를 반환한다.
 * router guard에서 사용한다 (URL 직접 진입/새로고침 시 store.userInfo가 비어 있을 수 있음).
 *
 * 중요: getPrivileges는 username(/privileges/{username})으로 조회한다. JWT sub는 companyId라
 * username이 아니다. getCompanyInfo(/companies/{companyId})로 userInfo.userId(=username)를
 * 먼저 확보한 뒤 getPrivileges를 호출한다. (ForcePasswordChange.goToAppHome과 동일 패턴)
 *
 * @param {Object} store - Vuex store 인스턴스
 * @param {boolean} [force=false] - 캐시 무시 여부
 * @returns {Promise<string>} 진입 경로
 */
export async function resolveLandingPathForUser(store, force = false) {
  try {
    let username = store.state.userInfo?.userId || '';
    if (!username) {
      try { await store.dispatch('getCompanyInfo'); } catch (e) { /* noop: 회사정보 실패해도 진입 시도 */ }
      username = store.state.userInfo?.userId || '';
    }
    if (!username) return NO_MENU_PATH;
    const priv = await store.dispatch('getPrivileges', { username, force });
    return resolveLandingPath(priv);
  } catch (e) {
    console.warn('[landing] resolveLandingPathForUser 실패, no-menu로 폴백:', e);
    return NO_MENU_PATH;
  }
}

/**
 * privileges 문서에서 모든 메뉴 링크를 평탄화해 반환한다.
 * @param {Object|null} priv - getPrivileges 반환값
 * @returns {string[]} 메뉴 link 배열
 */
export function collectMenuLinks(priv) {
  const categories = priv?.category || [];
  const links = [];
  for (const cat of categories) {
    for (const menu of cat?.menu || []) {
      if (menu?.link) links.push(menu.link);
    }
  }
  return links;
}

/**
 * 사용자의 privileges 메뉴에 특정 링크가 포함되는지 검사한다.
 * 라우터 가드에서 권한 게이팅 대상 경로(수기 작업지시서 위저드 등) 접근 통제에 사용.
 * privileges 조회 실패 시 false(차단)로 폴백한다 — 권한 판별 불가 상태에서 개방하지 않는다.
 *
 * @param {Object} store - Vuex store 인스턴스
 * @param {string} link - 검사할 메뉴 링크 (예: '/certificate-work-order')
 * @returns {Promise<boolean>}
 */
export async function userHasMenuLink(store, link) {
  try {
    let username = store.state.userInfo?.userId || '';
    if (!username) {
      try { await store.dispatch('getCompanyInfo'); } catch (e) { /* noop */ }
      username = store.state.userInfo?.userId || '';
    }
    if (!username) return false;
    const priv = await store.dispatch('getPrivileges', { username, force: false });
    return collectMenuLinks(priv).includes(link);
  } catch (e) {
    console.warn('[landing] userHasMenuLink 실패, 차단으로 폴백:', e);
    return false;
  }
}

/**
 * 사용자가 prefix로 시작하는 메뉴 링크를 하나라도 보유했는지 검사한다.
 * GEO처럼 화면 대부분이 딥링크 전용(정확히 일치하는 메뉴 없음)인 화면군의
 * URL 직접 접근 게이팅에 쓴다 — 그 계열 메뉴가 하나라도 있으면 계열 전체 접근 허용.
 * 실패는 차단으로 폴백(fail-closed) — userHasMenuLink와 동일 원칙.
 *
 * @param {Object} store - Vuex store 인스턴스
 * @param {string} prefix - 검사할 링크 prefix (예: '/china/geo')
 * @returns {Promise<boolean>}
 */
export async function userHasMenuLinkPrefix(store, prefix) {
  try {
    let username = store.state.userInfo?.userId || '';
    if (!username) {
      try { await store.dispatch('getCompanyInfo'); } catch (e) { /* noop */ }
      username = store.state.userInfo?.userId || '';
    }
    if (!username) return false;
    const priv = await store.dispatch('getPrivileges', { username, force: false });
    return collectMenuLinks(priv).some(
      (link) => link === prefix || String(link).startsWith(`${prefix}/`)
    );
  } catch (e) {
    console.warn('[landing] userHasMenuLinkPrefix 실패, 차단으로 폴백:', e);
    return false;
  }
}
